<template>
  <!-- Card View -->
  <template v-if="!isList">
    <div
      :class="['task-session card', { wide }]"
      @click="handleSessionInfoClick($event)"
    >

    <div class="statistics">

      <div class="task-session__header">
        <div class="left">
          <div class="icon-container">
            <!-- Manage type icons -->
            <v-icon
              v-if="isCloud"
              v-tooltip="'Cloud Hosted Task'"
              size="20"
              color="#666"
              style="margin-left: 12px;"
            >
              mdi-cloud
            </v-icon>
            <v-icon
              v-if="isUserManaged"
              v-tooltip="'User Managed Task'"
              size="20"
              color="#666"
              style="margin-left: 12px;"
            >
              mdi-laptop
            </v-icon>
            <v-icon
              v-if="isKubernetes"
              v-tooltip="'Kubernetes Managed Task'"
              size="20"
              color="#666"
              style="margin-left: 12px;"
            >
              mdi-kubernetes
            </v-icon>
          </div>
        </div>
        <div v-if="showIcons" class="right">
          <div class="icon-container">
            <IconButton
              v-tooltip="`Refresh task`"
              class="icon refresh-icon"
              :icon="'refresh'"
              :width="20"
              :height="20"
              :type="'click'"
              @click.stop="handleRefresh()"
            />
            <IconButton
              v-tooltip="`Logs`"
              class="icon logs-icon"
              :icon="'log'"
              :width="20"
              :height="20"
              :type="'click'"
              @click.stop="handleLogsClick()"
            />
            <IconButton
              v-if="permissions && permissions.trainer_write"
              v-tooltip="`Terminate task. This will stop the task, but you can still view artifacts`"
              class="icon stop-icon"
              :icon="'stop_circle'"
              :width="22"
              :height="22"
              :type="'click'"
              @click.stop="terminateTask(session)"
            />
            <IconButton
              v-if="permissions && permissions.trainer_write"
              v-tooltip="`Move task to recycle bin`"
              class="icon delete delete-icon"
              :icon="'trash'"
              :width="22"
              :height="22"
              :type="'click'"
              @click.stop="deleteTask()"
            />
          </div>
        </div>
      </div>

      
    <div class="body">
        <div class="body__header">
          <div class="owner-id-display">
            <UserIcon
              :user="userList?.find((e) => e.username === session.username) || { username: session.username }"
              :size="'32px'"
            />
            <EncodedIdField
              :id="session.id"
              :type="'task'"
              :hideLabel="false"
            />

            <!-- Error/Terminated cross icon and Circular Progress -->
            <TaskProgress  style="width: 180px"
              :status="sessionStatus || session.status_info?.status"
              :percent="session.status_info?.percent || 0"
              :stage-label="session.status_info?.stage_label"
              :stage-number="session.status_info?.stage_number"
              :total-stages="session.status_info?.total_stages"
              :task-u-i-d="encodeID(session.id, 'bt-')"
              :user-name="session.username"
              :parent-u-i-d="encodeID(session.parent?.uid || session.parent?.id, session.type)"
            />
          </div>

          <div class="message-display">
            <div 
              v-if="(sessionStatus?.toLowerCase().includes('error') || sessionStatus?.toLowerCase().includes('terminate')) && session.message"
              class="message-value message-error"
            >
              {{ session.message }}
            </div>
            <div 
              v-else-if="session.status_info?.message"
              class="message-value message-success"
            >
              {{ session.status_info.message }}
            </div>
            <div
              v-else-if="sessionStatus"
              :class="['message-value', getCardStatusClass(sessionStatus)]"
            >
              {{ getCardStatusText(sessionStatus) }}
            </div>
          </div>

        </div>
     
        <hr>
        <div class="body__grid scrollbar">
          <!-- Dynamic Statistics Fields -->
          <div 
            v-for="field in statisticsFields"
            :key="field.label"
            class="statistics-field"
          >
            <div class="description">{{ field.label }}</div>
            <div class="value">
              <span 
                v-if="field.isHyperlink"
                v-tooltip="field.tooltip || field.value"
                class="hyperlink"
                style="cursor: pointer;"
                @click.stop="field.onClick(field.value)"
              >
                {{ field.value }}
              </span>
              <span 
                v-else
                v-tooltip="field.tooltip || ''"
                :class="{ 'duration': field.label === 'Duration', 'task-name-value': field.label === 'Name' }"
              >
                {{ field.value }}
              </span>
            </div>
          </div>
        </div> 

        <!-- Process ID -->
        <div class="process-id-container">
          <span class="process-id-label">Process ID:</span>
          <span 
            v-tooltip="session.cloud_instance_id"
            class="process-id-value"
          >
            {{ session.cloud_instance_id }}
          </span>
          <IconButton
            v-tooltip="'Copy Process ID'"
            class="icon copy-icon"
            :icon="'copy'"
            :width="14"
            :height="14"
            :type="'click'"
            @click.stop="copyProcessId"
          />
        </div>

      </div> 




    </div>
    </div>
  </template>

  <!-- List View -->
  <template v-else>
    <div class="task-session-list" @click="handleSessionInfoClick($event)">
      <!-- User Icon -->
      <div class="list-col user-col">
        <UserIcon
          :user="userList?.find((e) => e.username === session.username) || { username: session.username }"
          :size="'38px'"
        />
      </div>

      <!-- Task ID -->
      <div class="list-col id-col">
        <EncodedIdField
          :id="session.id"
          :type="'task'"
          :hideLabel="false"
        />
      </div>

      <!-- Name and Type -->
      <div class="list-col info-col white-bg">
        <div class="list-row">
          <span class="label">Name:</span>
          <span class="value" :title="session.name">{{ session.name || 'Unknown' }}</span>
        </div>
        <div class="list-row">
          <span class="label">Type:</span>
          <span class="value">{{ session.type }}{{ session.subtype ? '.' + session.subtype : '' }}</span>
        </div>
      </div>

      <!-- Status and Message -->
      <div class="list-col status-col white-bg">
        <div class="list-row">
          <span class="status-badge" :class="sessionStatus?.toLowerCase()">
            {{ sessionStatus }}
          </span>
        </div>
        <div v-if="session.appname" class="list-row">
          <span class="label">App:</span>
          <span class="value" :title="session.appname">{{ session.appname }}</span>
        </div>
        <!-- <div class="list-row message-row">
          <span class="value message-text" :title="session.message">
            {{ session.message || session.status_info?.message || '-' }}
          </span>
        </div> -->
      </div>

      <!-- Start Date and Duration -->
      <div class="list-col date-col white-bg">
        <div class="list-row">
          <span class="label">Start:</span>
          <span class="value">{{ formatTime(session.date) }}</span>
        </div>
        <div class="list-row">
          <span class="label">Duration:</span>
          <span class="value">{{ getEndTime(session) || '-' }}</span>
        </div>
      </div>

      <!-- Parent Name and UID -->
      <div class="list-col parent-col white-bg">
        <div v-if="session.parent && session.parent.id" class="list-row">
          <span class="label">{{ session.type === 'validation' ? 'Validator' : session.type === 'trainer' ? 'Trainer' : 'Activity' }}:</span>
          <span 
            class="value hyperlink"
            style="cursor: pointer;"
            @click.stop="handleNavigateToActivity(session.parent.uid || session.parent.name || session.parent.id)"
          >
            {{ session.parent.uid || session.parent.name || session.parent.id }}
          </span>
        </div>
        <div v-if="session.parent && session.parent.id" class="list-row parent-desc">
          <span class="desc-text">{{ session.parent.name || 'N/A' }}</span>
        </div>
      </div>

      <!-- Progress Component -->
      <div class="list-col progress-col">
        <TaskProgress
          :status="sessionStatus"
          :percent="session.status_info?.percent || 0"
          :stage-label="session.status_info?.stage_label"
          :stage-number="session.status_info?.stage_number"
          :total-stages="session.status_info?.total_stages"
        />
      </div>

      <!-- Action Icons -->
      <div v-if="showIcons" class="list-col actions-col white-bg">
        <div class="list-row">
          <div class="icon-container">
            <IconButton
              v-tooltip="`Refresh task`"
              class="icon refresh-icon"
              :icon="'refresh'"
              :width="18"
              :height="18"
              :type="'click'"
              @click.stop="handleRefresh()"
            />
            <IconButton
              v-tooltip="`Logs`"
              class="icon logs-icon"
              :icon="'log'"
              :width="18"
              :height="18"
              :type="'click'"
              @click.stop="handleLogsClick()"
            />
          </div>
        </div>
  
        <div class="list-row">
          <div class="icon-container">
            <IconButton
              v-if="permissions && permissions.trainer_write"
              v-tooltip="`Terminate task`"
              class="icon stop-icon"
              :icon="'stop_circle'"
              :width="18"
              :height="18"
              :type="'click'"
              @click.stop="terminateTask(session)"
            />
            <IconButton
              v-if="permissions && permissions.trainer_write"
              v-tooltip="`Delete task`"
              class="icon delete delete-icon"
              :icon="'trash'"
              :width="18"
              :height="18"
              :type="'click'"
              @click.stop="deleteTask()"
            />
          </div>
        </div>
      </div>

      <!-- Pause Resume Button -->
      <div v-if="props.showPause" class="list-col pause-col white-bg">
        <div class="list-row">
          <div class="icon-container">
            <IconButton
              v-tooltip="props.isPaused ? `Resume Status Update` : `Pause Status Update`"
              class="icon pause-icon"
              :icon="props.isPaused ? 'play' : 'pause'"
              :width="20"
              :height="20"
              :type="'click'"
              @click.stop="handlePauseResume()"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import IconButton from '@/components/IconButton.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import EncodedIdField from '@/components/EncodedIdField.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';
import UserIcon from '@/components/UserIcon.vue';
import TaskProgress from '@/components/Apps/TaskProgress.vue';
import { encodeID, decodeID } from '@/assets/js/utils.js';
import { goto_experiment, goto_trainer_session, goto_validation_session } from '@/assets/js/links.js';

const props = defineProps({
  session: {
    type: Object,
    default: () => {},
  },
  permissions: {
    type: Object,
    default: () => {},
  },
  isList: {
    type: Boolean,
    default: false,
  },
  showIcons: {
    type: Boolean,
    default: true,
  },
  showPause: {
    type: Boolean,
    default: false,
  },
  isPaused: {
    type: Boolean,
    default: false,
  },
  wide: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['info-clicked', 'delete-session', 'refresh-session', 'select-session', 'terminate-session', 'pause-resume']);

const $toast = useToast();
const store = useStore();
const router = useRouter();

const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl);
const show_menu = ref(false);
const hideProgress = ref(false);
const sessionStatus = ref("");

const showProgress = computed(() => {
  const dt = props.session.docker_task;
  if (dt.status?.toLowerCase().includes('complete')) return false;
  if (dt.data.length < 4) return false;
  return true;
});

const userList = computed(() => store.state.user.userList);

// Manage type helpers
const isCloud = computed(() => {
  return props.session?.manage_type === '' || props.session?.manage_type?.includes('aws');
});
const isUserManaged = computed(() => {
  return props.session?.manage_type === 'user';
});
const isKubernetes = computed(() => {
  return props.session?.manage_type === 'kubernetes';
});

const statisticsFields = computed(() => {
  const fields = [];

  // Helper function to add or update field by label
  const addOrUpdateField = (fieldToAdd) => {
    const existingIndex = fields.findIndex(f => f.label === fieldToAdd.label);
    if (existingIndex !== -1) {
      fields[existingIndex] = fieldToAdd; // Overwrite existing
    } else {
      fields.push(fieldToAdd); // Add new
    }
  };

  // Name field
  addOrUpdateField({
    label: 'Name',
    value: props.session && props.session.name ? props.session.name : "Unknown",
    isHyperlink: false,
    tooltip: props.session && props.session.name ? props.session.name : 'Unknown'
  });

  // App Name field
  if (props.session?.appname) {
    addOrUpdateField({
      label: 'App Name',
      value: props.session.appname,
      isHyperlink: false,
      tooltip: props.session.appname
    });
  }

  // Type field
  if (props.session?.type) {
    addOrUpdateField({
      label: 'Type',
      value: `${props.session.type}${props.session.subtype ? '.' + props.session.subtype : ''}`,
      isHyperlink: false
    });
  }

  // Parent Activity field
  if (props.session?.parent && props.session.parent.id) {
    const parentLabel = props.session.type === 'validation' ? 'Validator' : props.session.type === 'trainer' ? 'Trainer' : 'Activity';
    let parentValue = '';
    if ((props.session.type === 'trainer' || props.session.type === 'validation') && props.session.parent.uid) {
      parentValue = props.session.parent.uid;
    } else if (props.session.parent.name) {
      parentValue = props.session.parent.name;
    } else {
      parentValue = `ID: ${props.session.parent.id}`;
    }
    
    addOrUpdateField({
      label: parentLabel,
      value: parentValue,
      isHyperlink: (props.session.type === 'trainer' || props.session.type === 'validation' || props.session.parent.name),
      onClick: handleNavigateToActivity,
      tooltip: props.session.parent.name || props.session.parent.uid
    });
  }

  // Trainer field - from train_session_id
  if (props.session?.train_session_id && !(props.session?.type === 'trainer' && props.session?.parsed_data?.training_session?.id)) {
    addOrUpdateField({
      label: 'Trainer',
      value: encodeID(props.session.train_session_id, 't'),
      isHyperlink: true,
      onClick: handleNavigateToTrainerSession
    });
  }

  // Parent Trainer field - if parent is trainer type
  if (props.session?.parent?.type === 'trainer' && props.session?.parent?.id && 
      props.session.parent.id !== props.session.train_session_id) {
    addOrUpdateField({
      label: 'Trainer',
      value: encodeID(props.session.parent.id, 't'),
      isHyperlink: true,
      onClick: handleNavigateToActivity
    });
  }

  // Validation field - from val_session_id
  if (props.session?.val_session_id && !(props.session?.type === 'validation' && props.session?.parsed_data?.validation_session?.id)) {
    addOrUpdateField({
      label: 'Validator',
      value: encodeID(props.session.val_session_id, 'v'),
      isHyperlink: true,
      onClick: handleNavigateToValidatorSession
    });
  }

  // Parent Validation field - if parent is validation type
  if (props.session?.parent?.type === 'validation' && props.session?.parent?.id && 
      props.session.parent.id !== props.session.val_session_id) {
    addOrUpdateField({
      label: 'Validator',
      value: encodeID(props.session.parent.id, 'v'),
      isHyperlink: true,
      onClick: handleNavigateToActivity
    });
  }

  // Training Session field
  if (props.session?.type === 'trainer' && props.session?.parsed_data?.training_session?.id) {
    addOrUpdateField({
      label: 'Trainer',
      value: props.session.parsed_data.training_session.name || '',
      isHyperlink: true,
      onClick: handleNavigateToTrainingSession,
      tooltip: props.session.parsed_data.training_session.name
    });
  }

  // Start Date field
  addOrUpdateField({
    label: 'Start Date',
    value: formatTime(props.session?.date || ''),
    isHyperlink: false
  });

  // Duration field
  addOrUpdateField({
    label: 'Duration',
    value: getEndTime(props.session) || '-',
    isHyperlink: false
  });

  return fields;
});

watch(
  () => props.session,
  (newVal) => {
    if (newVal) {
      getStatus(newVal);
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.session) {
    getStatus(props.session);
  }
});


const handleSessionInfoClick = (event) => {
  // If clicked on a parent link (hyperlink), let handleNavigateToActivity handle it
  if (event.target.closest('.hyperlink')) {
    return;
  }
  
  emit('info-clicked');
  // Navigate to task details page
  let taskUid = props.session?.uid;
  if (!taskUid && props.session?.id) {
    // Convert numeric id to hex format with bt- prefix
    const hexId = props.session.id.toString(16);
    taskUid = `bt-${hexId}`;
  }
  if (taskUid) {
    router.push({ name: 'task-details', params: { task_uid: taskUid } });
  }
};

const getStatus = (session) => {
  sessionStatus.value = session.status;
};

const getCardStatusClass = (status) => {
  const normalizedStatus = status?.toLowerCase() || '';

  if (normalizedStatus.includes('complete')) {
    return 'message-success';
  }

  if (normalizedStatus.includes('terminate') || normalizedStatus.includes('deleted')) {
    return 'message-error';
  }

  return 'message-waiting';
};

const getCardStatusText = (status) => {
  if (!status) {
    return '';
  }

  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus.includes('complete')) {
    return 'Completed';
  }

  if (normalizedStatus.includes('deleted')) {
    return 'Deleted';
  }

  if (normalizedStatus.includes('terminate')) {
    return 'Terminated';
  }

  return status;
};

const handleNavigateToActivity = (linkValue) => {
  if (typeof linkValue === 'string') {
    if (linkValue.startsWith('bt-')) {
      router.push({ name: 'task-details', params: { task_uid: linkValue } });
      return;
    }

    if (linkValue.startsWith('t-')) {
      navigateToTrainingTarget({
        id: decodeID(linkValue),
        project_id: props.session.project_id || props.session.parent?.project_id,
        experiment_id: props.session.experiment_id || props.session.parent?.experiment_id,
      });
      return;
    }

    if (linkValue.startsWith('v-')) {
      const validationId = decodeID(linkValue);
      if (validationId) {
        goto_validation_session(
          props.session.project_id || props.session.parent?.project_id,
          props.session.experiment_id || props.session.parent?.experiment_id,
          validationId,
        );
        return;
      }
    }
  }
};

const navigateToTrainingTarget = (trainingTarget) => {
  if (!trainingTarget?.id) {
    return;
  }

  if (trainingTarget.project_id && trainingTarget.experiment_id) {
    goto_experiment(trainingTarget.project_id, trainingTarget.experiment_id);
    return;
  }

  goto_trainer_session(trainingTarget.project_id, trainingTarget.experiment_id, trainingTarget.id);
};

const handleNavigateToTrainingSession = () => {
  if (props.session?.parsed_data?.training_session?.id) {
    navigateToTrainingTarget(props.session.parsed_data.training_session);
  }
};

const handleNavigateToValidationSession = () => {
  if (props.session?.type === 'validation' && props.session?.parent?.id) {
    // For validation tasks, parent is the validation session
    router.push({ name: 'experiment.validation.details', params: { sessionId: encodeID(props.session.parent.id, 'v') } });
  } else if (props.session?.parsed_data?.validation_session?.id) {
    router.push({ name: 'experiment.validation.details', params: { sessionId: encodeID(props.session.parsed_data.validation_session.id, 'v') } });
  }
};

const handleNavigateToTrainerSession = () => {
  if (props.session?.train_session_id) {
    navigateToTrainingTarget({
      id: props.session.train_session_id,
      project_id: props.session.project_id || props.session.parent?.project_id,
      experiment_id: props.session.experiment_id || props.session.parent?.experiment_id,
    });
  }
};

const handleNavigateToValidatorSession = () => {
  if (props.session?.val_session_id) {
    router.push({ name: 'experiment.validation.details', params: { sessionId: encodeID(props.session.val_session_id, 'v') } });
  }
};


const getEndTime = (docker_task) => {
  if (docker_task.end_date !== "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = new Date(docker_task.end_date) - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else if (docker_task.end_date === "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = Date.now() - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else {
    return null;
  }
};


const formatTime = (str) => {
  return `${new Date(str).toLocaleTimeString('default', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })}`;
};

const deleteTask = async () => {
  try {
    const params = {
      docker_task_id: props.session.id
    };
    const response = await dsConn.deleteDockerTask(params);
    if (response && response.result) {
      emit('delete-session', props.session);
    }
  } catch (error) {
    console.error('Error deleting task:', error);
  }
};

const handleRefresh = async () => {
  try {
    const params = {
      id: props.session.id
    };
    const response = await dsConn.api("job.list", params);
    if (response && response.result) {
      // Optionally update the session data or emit to parent
      emit('refresh-session', response.result);
    }
  } catch (error) {
    console.error('Error refreshing task:', error);
  }
};

const handleLogsClick = () => {
  const uid = `bt-${props.session.id.toString(16)}`;
  router.push({ name: 'logs', params: { task_id: uid } });
};

const handlePauseResume = () => {
  emit('pause-resume', !props.isPaused);
};

const terminateTask = async (session) => {
  try {
    const params = {
      task_id: session.id
    };
    const response = await dsConn.api("job.stop", params);
    if (response && response.result) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Termination request submitted',
        type: 'success',
        position: 'top'
      });
      emit('terminate-session', session);
    }
    if (response && response.error) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: response.error.message || 'Failed to terminate task',
        type: 'error',
        position: 'top'
      });
    }
  } catch (error) {
    console.error('Error terminating task:', error);
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'Failed to terminate task',
      type: 'error',
      position: 'top'
    });
  }
};

const copyProcessId = async () => {
  if (props.session.cloud_instance_id) {
    try {
      await navigator.clipboard.writeText(props.session.cloud_instance_id);
    } catch (error) {
      console.error('Failed to copy process ID:', error);
    }
  }
};

const navigateToTrainingSession = (id) => {
  // Note: router needs to be accessed via useRouter() in composition API
  // import { useRouter } from 'vue-router';
  // const router = useRouter();
  // router.push({ name: 'experiment.training.details', query: { train_session_id: id } });
};
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  position: relative;
  height: fit-content;
  margin: 0;
  text-align: left;
  border-radius: 8px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.task-session {
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  height: 330px;
  width: 350px;
  padding: 0;
  cursor: pointer;

  &.wide {
    width: 560px;
    height: fit-content;

    .body__grid {
      .description {
        font-size: 1rem;
      }
      .value {
        font-size: 1rem;
      }
    }
  }

  &__header {
    display: flex;
    flex-direction: row;
    padding: 4px 10px 4px 0px;
    background: var(--gray-200);
    height: 32px;
  }

  .progress {
    display: flex;
    flex-direction: column;
    height: 100%;
    //width: 40%;
    width: 350px;
  }
  .statistics {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }

  .left {
      width: 50%;
      min-width: 50%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }

  .task-type {
    font-size: 12px;
    font-weight: 500;
    color: var(--body-text-color);
    padding: 2px 6px;
    background: var(--gray-100);
    border-radius: 4px;
  }

  .right {
    width: 50%;
    min-width: 50%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end
  }
}

.section-title {
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 1rem;
  font-weight: 700;
  color: var(--body-text-color-heading);
  letter-spacing: 0.025em;
  gap: 0px;
}

.item-selector{
  width: fit-content;
  height: fit-content;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
}

.body {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  padding: 10px;
  overflow: hidden;

  &__header{
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    .owner-id-display {
      display: flex;
      flex-direction: row;
      gap: 10px;
      align-items: flex-start;
      width: 100%;
      min-height: 55px;

      .labels-container {
        flex: 1
      }
    }

    .task-name-value {
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
    }

    .message-display {
      margin-top: 4px;
      min-height: 1.1rem;
    }

    .message-value {
      font-size: 0.85rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }

    .name-display {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start
    }

    .description {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--body-text-color);
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__grid-field {
    display: block;
    max-width: 300px;
  }

  &__grid-field + &__grid-field {
    margin-top: 10px;
  }

  &__grid {
    display: block;

    .statistics-field {
      display: flex;
      flex-direction: row;
      gap: 10px;
      margin-top: 1px;
    }

    .description {
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--body-text-color);
      width: 120px;
      min-width: 120px;
    }
    .value {
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--body-text-color-secondary);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &._separator::after {
    content: "";
    position: absolute;
    right: 0;
    top: 5%;
    bottom: 5%;
    width: 1px;
    background-color: #9a9ca2;
  }
}

.icon-container {
  display: flex;
  align-items: center;
  margin-left: 4px;
  @include themify() {
    color: themed('body-text-color-secondary');
  }
  .icon + .icon {
    margin-left: 14px;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: -moz-fit-content;
  height: fit-content;
}

.icon {
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.icon:hover {
  @include themify() {
    color: themed('color-primary');
    background: themed('icon-hover-color');
    border-radius: 100%;
    box-shadow: 0 0 0 4px themed('icon-hover-color');
  }
}

.icon.delete:hover {
  color: var(--color-error)
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  min-width: 100px;
  border-radius: 18px;
  padding: 4px 6px;
  line-height: 1;
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid;
  box-shadow: 0px 0px 1px rgb(0 0 0 / 25%), 0px 1px 4px rgb(0 0 0 / 25%);
  color: #4a4a4a;

  &._complete {
    @include themify() {
      background: themed('color-status-complete-background');
      border-color: themed('color-status-complete');
    }
  }
  &._running {
    @include themify() {
      background: themed('color-status-running-background');
      border-color: themed('color-status-running');
    }
  }
  &._error {
    @include themify() {
      background: themed('color-status-error-background');
      border-color: themed('color-status-error');
    }
  }

  &._warning {
    @include themify() {
      background: themed('color-status-warning-background');
      border-color: themed('color-status-warning');
    }
  }

  .running-icon {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }

  .status-icon {
    color: inherit;
    margin-right: 4px;
  }
}

.session-id-field {
  margin-right: 20px;
}

:deep() {
  .v-expansion-panel__shadow {
    box-shadow: none !important;
  }
}

.task-sessions-empty {
  display: flex;
  flex-direction: column;
  height: 150px;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  border: solid 1px black;
  border-radius: 8px;
  @include themify() {
    background: radial-gradient(circle, rgba($color-primary-300, 0.075) 50%, rgba(var(--color-primary-rgb), 0.125) 100%);
  }

  span {
    padding: 16px;
    text-align: center;
  }
}

.select-checkbox{
  position: relative !important;
  top: -7px;
  left: -5px;
}

.process-id-container {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  margin-top: auto;
  font-size: 9px;
  color: var(--body-text-color-secondary, #888);
  border-top: 1px solid var(--gray-200);
  padding-top: 6px;
}

.process-id-label {
  flex-shrink: 0;
  font-weight: 500;
}

.process-id-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.copy-icon {
  flex-shrink: 0;
  opacity: 0.6;
  
  &:hover {
    opacity: 1;
  }
}

.message-field {
  grid-column: 1 / -1;
  
  .value {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
}

.message-error {
  color: #d32f2f !important;
}

.message-success {
  color: #388e3c !important;
}

.message-waiting {
  color: #0d57ab !important;
}

/* List View Styles */
.task-session-list {
  display: flex;
  flex-direction: row;
  gap: 2px;
  height: 60px;
  padding: 2px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  align-items: center;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  background: white;
  overflow: hidden;
}

.list-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;

  &.white-bg {
    background: white;
    border-radius: 4px;
    padding: 6px 10px;
  }

  &.user-col {
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    min-width: 60px;
    width: 60px;
  }

  &.id-col {
    flex-shrink: 0;
    min-width: 140px;
  }

  &.info-col {
    flex: 1;
    min-width: 150px;
  }

  &.status-col {
    flex: 0.8;
    min-width: 120px;
  }

  &.date-col {
    flex: 1;
  }

  &.parent-col {
    flex: 1;
    min-width: 120px;
  }

  &.progress-col {
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
  }

  &.actions-col {
    flex-shrink: 0;
    min-width: 100px;
  }

  &.pause-col {
    flex-shrink: 0;
    width: 40px;
    padding: 6px 6px;
    margin-right: 12px;
  }
}

.list-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .label {
    font-weight: 600;
    color: #232323;
    flex-shrink: 0;
  }

  .value {
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;

    &.message-text {
      max-width: 100%;
      white-space: normal;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      flex: 1;
    }
  }

  .hyperlink {
    color: #0066cc;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;

    &.completed,
    &.complete {
      background: #d4edda;
      color: #155724;
    }

    &.error {
      background: #f8d7da;
      color: #721c24;
    }

    &.queued {
      background: #cfe2ff;
      color: #084298;
    }

    &.running,
    &.init {
      background: #cfe2ff;
      color: #084298;
    }

    &.terminated,
    &.terminate {
      background: #fff3cd;
      color: #856404;
    }

    &.unknown {
      background: #e2e3e5;
      color: #383d41;
    }
  }

  .icon-container {
    display: flex;
    gap: 4px;
    align-items: center;

    .icon {
      flex-shrink: 0;
    }
  }

  &.message-row {
    min-height: 18px;
  }

  &.parent-desc {
    width: 100%;
  }
}

.parent-desc {
  .desc-text {
    font-size: 11px;
    font-style: italic;
    color: #888;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

</style>
